// =========================================================
// CabinsGallery.jsx
// ORION BEACH HOTEL · KITE HOUSE
// CABINS GALLERY
//
// EDITORIAL · BOUTIQUE · PREMIUM · PINTEREST
// VERSION 3.0
// =========================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "../styles/results.css";

// =========================================================
// API
// =========================================================

const API = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.DEV
      ? "http://127.0.0.1:8000"
      : "https://hotelorionmayapo.com"
  )
).replace(/\/+$/, "");

// =========================================================
// WHATSAPP
// =========================================================

const DEFAULT_PHONE = "573104794943";

// =========================================================
// DEFAULT CABINS
// =========================================================

const DEFAULT_CABINS = [
  {
    id: "default-1",
    title: "Cabaña Orión",
    category: "Cabañas",
    description:
      "Un refugio frente al Caribe para descansar, desconectarte y disfrutar la magia de La Guajira.",
    image: "/images/cabanas/cabana-1.png",
    imageAlt: "Cabaña Orión frente al mar",
    featured: true,
  },

  {
    id: "default-2",
    title: "Frente al Mar",
    category: "Mar",
    description:
      "Despierta con la brisa del Caribe y disfruta una experiencia única frente al mar.",
    image: "/images/cabanas/cabana-2.png",
    imageAlt: "Cabaña frente al mar",
    featured: false,
  },

  {
    id: "default-3",
    title: "Kite House",
    category: "Kitesurf",
    description:
      "El punto perfecto para combinar descanso, aventura, viento y kitesurf.",
    image: "/images/cabanas/cabana-3.png",
    imageAlt: "Kite House Orión Beach Hotel",
    featured: true,
  },

  {
    id: "default-4",
    title: "Tropical",
    category: "Naturaleza",
    description:
      "Naturaleza, tranquilidad y el ambiente único de la costa de La Guajira.",
    image: "/images/cabanas/cabana-4.png",
    imageAlt: "Cabaña tropical Orión",
    featured: false,
  },

  {
    id: "default-5",
    title: "Familiar",
    category: "Familias",
    description:
      "Un espacio pensado para compartir momentos especiales con quienes más quieres.",
    image: "/images/cabanas/cabana-5.png",
    imageAlt: "Cabaña familiar Orión",
    featured: true,
  },

  {
    id: "default-6",
    title: "Premium",
    category: "Premium",
    description:
      "Privacidad, comodidad y una experiencia especial frente al Caribe.",
    image: "/images/cabanas/cabana-6.png",
    imageAlt: "Cabaña premium Orión",
    featured: false,
  },

  {
    id: "default-7",
    title: "Premium",
    category: "Premium",
    description:
      "Privacidad, comodidad y una experiencia especial frente al Caribe.",
    image: "/images/cabanas/cabana-8.png",
    imageAlt: "Cabaña premium Orión",
    featured: true,
  },

  {
    id: "default-8",
    title: "Premium",
    category: "Premium",
    description:
      "Privacidad, comodidad y una experiencia especial frente al Caribe.",
    image: "/images/cabanas/cabana-7.png",
    imageAlt: "Cabaña premium Orión",
    featured: false,
  },
];

// =========================================================
// NORMALIZE IMAGE URL
// =========================================================

const normalizeImageUrl = (url) => {
  if (!url) {
    return "";
  }

  const value = String(url).trim();

  if (!value) {
    return "";
  }

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (
    value.startsWith("/images/") ||
    value.startsWith("/assets/")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API}${value}`;
  }

  return `${API}/${value.replace(/^\/+/, "")}`;
};

// =========================================================
// GET IMAGE
// =========================================================

const getItemImage = (item) => {
  if (!item) {
    return "";
  }

  const directImage =
    item.image ||
    item.image_url ||
    item.imageUrl ||
    item.photo ||
    item.photo_url ||
    item.photoUrl ||
    item.thumbnail ||
    item.thumbnail_url ||
    item.src ||
    item.url;

  if (directImage) {
    return normalizeImageUrl(directImage);
  }

  if (
    Array.isArray(item.images) &&
    item.images.length
  ) {
    const first = item.images[0];

    if (typeof first === "string") {
      return normalizeImageUrl(first);
    }

    if (
      first &&
      typeof first === "object"
    ) {
      return normalizeImageUrl(
        first.url ||
        first.image ||
        first.image_url ||
        first.src ||
        first.thumbnail
      );
    }
  }

  return "";
};

// =========================================================
// GET PHONE
// =========================================================

const getPhone = (cabin) => {
  const phone =
    cabin?.phone ||
    cabin?.whatsapp ||
    cabin?.whatsapp_phone ||
    DEFAULT_PHONE;

  return String(phone)
    .replace(/\D/g, "") || DEFAULT_PHONE;
};

// =========================================================
// NORMALIZE API
// =========================================================

const normalizeCabins = (data) => {
  let items = [];

  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data?.items)) {
    items = data.items;
  } else if (
    Array.isArray(data?.content?.items)
  ) {
    items = data.content.items;
  } else if (
    Array.isArray(data?.results)
  ) {
    items = data.results;
  } else if (
    Array.isArray(data?.data)
  ) {
    items = data.data;
  }

  return items
    .map((item, index) => {
      const image = getItemImage(item);

      return {
        ...item,

        id:
          item?.id ||
          item?.slug ||
          `api-${index}`,

        title:
          item?.title ||
          item?.name ||
          item?.label ||
          `Cabaña ${index + 1}`,

        category:
          item?.category ||
          item?.type ||
          item?.category_name ||
          "Cabañas",

        description:
          item?.description ||
          item?.short_description ||
          item?.excerpt ||
          "Disfruta una experiencia única en Orión Beach Hotel.",

        image,

        imageAlt:
          item?.imageAlt ||
          item?.image_alt ||
          item?.title ||
          item?.name ||
          "Orión Beach Hotel",

        featured:
          Boolean(
            item?.featured ||
            item?.is_featured ||
            item?.highlighted
          ),

        phone: getPhone(item),
      };
    })
    .filter((item) => Boolean(item.image));
};

// =========================================================
// WHATSAPP URL
// =========================================================

const createWhatsAppUrl = (cabin) => {
  const phone = getPhone(cabin);

  const cabinName =
    cabin?.title ||
    "una de nuestras cabañas";

  const message =
    `Hola, quiero información y disponibilidad para reservar ${cabinName} en Orión Beach Hotel.`;

  return (
    `https://wa.me/${phone}` +
    `?text=${encodeURIComponent(message)}`
  );
};

// =========================================================
// GENERAL WHATSAPP
// =========================================================

const createGeneralWhatsAppUrl = () => {
  const message =
    "Hola, quiero información sobre las cabañas y disponibilidad en Orión Beach Hotel.";

  return (
    `https://wa.me/${DEFAULT_PHONE}` +
    `?text=${encodeURIComponent(message)}`
  );
};

// =========================================================
// WHATSAPP ICON
// =========================================================

function WhatsAppIcon() {
  return (
    <svg
      viewBox="0 0 32 32"
      aria-hidden="true"
      className="whatsapp-icon"
    >
      <path
        d="M16 3C8.82 3 3 8.72 3 15.78c0 2.53.75 4.88 2.04 6.86L3.2 29l6.55-1.78A13.03 13.03 0 0 0 16 28.56c7.18 0 13-5.72 13-12.78S23.18 3 16 3Zm0 23.34c-2.05 0-4.05-.55-5.8-1.6l-.42-.25-3.89 1.06 1.04-3.75-.27-.4a10.6 10.6 0 0 1-1.65-5.62C5.01 10.03 9.94 5.2 16 5.2s10.99 4.83 10.99 10.78S22.06 26.34 16 26.34Zm6.02-8.06c-.33-.16-1.94-.95-2.24-1.06-.3-.11-.52-.16-.74.16-.22.33-.85 1.06-1.04 1.28-.19.22-.38.25-.71.08-.33-.16-1.39-.51-2.65-1.64-.98-.88-1.64-1.96-1.83-2.29-.19-.33-.02-.5.14-.66.15-.15.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.03-.57-.08-.16-.74-1.78-1.02-2.44-.27-.65-.54-.56-.74-.57h-.63c-.22 0-.57.08-.87.41-.3.33-1.14 1.11-1.14 2.71s1.17 3.14 1.33 3.36c.16.22 2.3 3.52 5.58 4.94.78.34 1.39.54 1.87.69.79.25 1.5.21 2.06.13.63-.09 1.94-.79 2.21-1.55.27-.76.27-1.41.19-1.55-.08-.14-.3-.22-.63-.38Z"
      />
    </svg>
  );
}

// =========================================================
// ARROW ICON
// =========================================================

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="cabin-arrow-icon"
    >
      <path
        d="M5 19 19 5M8 5h11v11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// =========================================================
// CLOSE ICON
// =========================================================

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="cabin-close-icon"
    >
      <path
        d="M6 6l12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

// =========================================================
// COMPONENT
// =========================================================

export default function CabinsGallery() {
  // =======================================================
  // STATES
  // =======================================================

  const [cabins, setCabins] =
    useState(DEFAULT_CABINS);

  const [loading, setLoading] =
    useState(true);

  const [activeFilter, setActiveFilter] =
    useState("Todas");

  const [selectedCabin, setSelectedCabin] =
    useState(null);

  const [imageErrors, setImageErrors] =
    useState({});

  const modalCloseRef =
    useRef(null);

  // =======================================================
  // LOAD API
  // =======================================================

  useEffect(() => {
    let mounted = true;

    const loadCabins = async () => {
      try {
        const response = await fetch(
          `${API}/api/content/results`
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`
          );
        }

        const data =
          await response.json();

        const apiCabins =
          normalizeCabins(data);

        if (
          mounted &&
          apiCabins.length > 0
        ) {
          setCabins(apiCabins);
        }
      } catch (error) {
        console.warn(
          "API de cabañas no disponible. Utilizando contenido local.",
          error
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCabins();

    return () => {
      mounted = false;
    };
  }, []);

  // =======================================================
  // CATEGORIES
  // =======================================================

  const categories = useMemo(() => {
    const values = cabins
      .map(
        (cabin) =>
          cabin.category
      )
      .filter(Boolean);

    return [
      "Todas",
      ...new Set(values),
    ];
  }, [cabins]);

  // =======================================================
  // FILTERED CABINS
  // =======================================================

  const filteredCabins = useMemo(() => {
    if (
      activeFilter === "Todas"
    ) {
      return cabins;
    }

    return cabins.filter(
      (cabin) =>
        cabin.category ===
        activeFilter
    );
  }, [
    cabins,
    activeFilter,
  ]);

  // =======================================================
  // OPEN CABIN
  // =======================================================

  const openCabin = useCallback(
    (cabin) => {
      setSelectedCabin(cabin);
    },
    []
  );

  // =======================================================
  // CLOSE CABIN
  // =======================================================

  const closeCabin = useCallback(() => {
    setSelectedCabin(null);
  }, []);

  // =======================================================
  // ESCAPE
  // =======================================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        selectedCabin
      ) {
        closeCabin();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    selectedCabin,
    closeCabin,
  ]);

  // =======================================================
  // MODAL FOCUS
  // =======================================================

  useEffect(() => {
    if (!selectedCabin) {
      return undefined;
    }

    const timer = setTimeout(() => {
      modalCloseRef.current?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [selectedCabin]);

  // =======================================================
  // LOCK BODY
  // =======================================================

  useEffect(() => {
    if (!selectedCabin) {
      document.body.style.overflow = "";
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [selectedCabin]);

  // =======================================================
  // IMAGE ERROR
  // =======================================================

  const handleImageError = useCallback(
    (event, cabin, index) => {
      const key =
        cabin?.id ||
        `image-${index}`;

      if (imageErrors[key]) {
        return;
      }

      setImageErrors((previous) => ({
        ...previous,
        [key]: true,
      }));

      const fallback =
        DEFAULT_CABINS[
          index %
            DEFAULT_CABINS.length
        ]?.image;

      if (
        fallback &&
        event.currentTarget.src !==
          fallback
      ) {
        event.currentTarget.src =
          fallback;
      }
    },
    [imageErrors]
  );

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <section
      id="habitaciones"
      className="cabins"
    >
      {/* =================================================
          DECORATIVE BACKGROUND
      ================================================= */}

      <div
        className="cabins__orb cabins__orb--gold"
        aria-hidden="true"
      />

      <div
        className="cabins__orb cabins__orb--green"
        aria-hidden="true"
      />

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="cabins__header">
        <div className="cabins__intro">
          <div className="cabins__eyebrow">
            <span className="cabins__eyebrow-line" />

            <span>
              ORION BEACH HOTEL
            </span>

            <span className="cabins__eyebrow-dot" />
          </div>

          <h2 className="cabins__title">
            <span>
              Descansa
            </span>

            <em>
              frente al Caribe
            </em>
          </h2>

          <p className="cabins__description">
            Cabañas, habitaciones y
            espacios pensados para
            vivir el mar, el viento
            y la esencia de La Guajira.
          </p>
        </div>

        <div className="cabins__header-meta">
          <div className="cabins__meta-location">
            <span>
              KITE HOUSE
            </span>

            <strong>
              LA GUAJIRA
            </strong>
          </div>

          <div className="cabins__meta-divider" />

          <div className="cabins__meta-count">
            <strong>
              {String(
                cabins.length
              ).padStart(2, "0")}
            </strong>

            <span>
              ESPACIOS
            </span>
          </div>
        </div>
      </header>

      {/* =================================================
          INTRO STRIP
      ================================================= */}

      <div className="cabins__intro-strip">
        <span>
          01 / ESTANCIA
        </span>

        <p>
          Arquitectura natural,
          brisa marina y noches
          bajo el cielo guajiro.
        </p>

        <span>
          MAYAPO · COLOMBIA
        </span>
      </div>

      {/* =================================================
          FILTERS
      ================================================= */}

      <nav
        className="cabins__filters"
        aria-label="Categorías de alojamiento"
      >
        <div className="cabins__filters-left">
          <span className="cabins__filters-label">
            EXPLORAR
          </span>

          <div className="cabins__filters-inner">
            {categories.map(
              (category) => (
                <button
                  key={category}
                  type="button"
                  className={
                    activeFilter ===
                    category
                      ? "cabins__filter cabins__filter--active"
                      : "cabins__filter"
                  }
                  onClick={() =>
                    setActiveFilter(
                      category
                    )
                  }
                  aria-pressed={
                    activeFilter ===
                    category
                  }
                >
                  <span>
                    {category}
                  </span>
                </button>
              )
            )}
          </div>
        </div>

        <div className="cabins__filters-count">
          <strong>
            {String(
              filteredCabins.length
            ).padStart(2, "0")}
          </strong>

          <span>
            RESULTADOS
          </span>
        </div>
      </nav>

      {/* =================================================
          LOADING
      ================================================= */}

      {loading && (
        <div
          className="cabins__loading"
          aria-live="polite"
        >
          <div className="cabins__loading-mark">
            <span />
            <span />
            <span />
          </div>

          <div>
            <strong>
              ORION BEACH HOTEL
            </strong>

            <p>
              Descubriendo nuestras
              estancias...
            </p>
          </div>
        </div>
      )}

      {/* =================================================
          GRID
      ================================================= */}

      <div className="cabins__grid">
        {filteredCabins.map(
          (cabin, index) => {
            const isFeatured =
              Boolean(
                cabin.featured
              );

            return (
              <article
                key={
                  cabin.id ||
                  `cabin-${index}`
                }
                className={
                  isFeatured
                    ? "cabin cabin--featured"
                    : "cabin"
                }
                style={{
                  "--cabin-delay": `${
                    Math.min(
                      index * 70,
                      420
                    )
                  }ms`,
                }}
              >
                {/* =================================================
                    IMAGE
                ================================================= */}

                <button
                  type="button"
                  className="cabin__image-button"
                  onClick={() =>
                    openCabin(
                      cabin
                    )
                  }
                  aria-label={`Explorar ${cabin.title}`}
                >
                  <img
                    src={cabin.image}
                    alt={
                      cabin.imageAlt
                    }
                    className="cabin__image"
                    loading={
                      index < 3
                        ? "eager"
                        : "lazy"
                    }
                    decoding="async"
                    onError={(
                      event
                    ) =>
                      handleImageError(
                        event,
                        cabin,
                        index
                      )
                    }
                  />

                  <div className="cabin__image-shade" />

                  <div className="cabin__image-noise" />

                  {/* TOP */}
                  <div className="cabin__top">
                    <span className="cabin__category">
                      {cabin.category}
                    </span>

                    <span className="cabin__number">
                      {String(
                        index + 1
                      ).padStart(2, "0")}
                    </span>
                  </div>

                  {/* FEATURED */}
                  {isFeatured && (
                    <div className="cabin__featured">
                      <span />
                      DESTACADA
                    </div>
                  )}

                  {/* CENTER */}
                  <span className="cabin__explore">
                    <span>
                      Explorar
                    </span>

                    <ArrowIcon />
                  </span>

                  {/* CONTENT */}
                  <div className="cabin__overlay-content">
                    <span className="cabin__mini-label">
                      ORION BEACH HOTEL
                    </span>

                    <h3>
                      {cabin.title}
                    </h3>

                    <p>
                      {cabin.description}
                    </p>

                    <div className="cabin__discover">
                      <span>
                        Descubrir estancia
                      </span>

                      <span className="cabin__discover-line" />
                    </div>
                  </div>
                </button>

                {/* =================================================
                    FOOTER
                ================================================= */}

                <div className="cabin__footer">
                  <div className="cabin__footer-info">
                    <span>
                      DISPONIBILIDAD
                    </span>

                    <strong>
                      {cabin.title}
                    </strong>
                  </div>

                  <a
                    href={createWhatsAppUrl(
                      cabin
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cabin__whatsapp"
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                    aria-label={`Consultar ${cabin.title} por WhatsApp`}
                  >
                    <WhatsAppIcon />

                    <span>
                      Consultar
                    </span>

                    <strong>
                      →
                    </strong>
                  </a>
                </div>
              </article>
            );
          }
        )}
      </div>

      {/* =================================================
          EMPTY
      ================================================= */}

      {!loading &&
        filteredCabins.length ===
          0 && (
          <div className="cabins__empty">
            <span className="cabins__empty-number">
              00
            </span>

            <span className="cabins__empty-label">
              ORION BEACH HOTEL
            </span>

            <h3>
              No encontramos
              esta estancia.
            </h3>

            <p>
              No hay resultados
              disponibles para esta
              categoría.
            </p>

            <button
              type="button"
              onClick={() =>
                setActiveFilter(
                  "Todas"
                )
              }
            >
              Ver todas las estancias
            </button>
          </div>
        )}

      {/* =================================================
          CTA
      ================================================= */}

      <div className="cabins__cta">
        <div className="cabins__cta-glow" />

        <div className="cabins__cta-number">
          02
        </div>

        <div className="cabins__cta-copy">
          <span>
            TU PRÓXIMA ESCAPADA
          </span>

          <h3>
            El Caribe
            <em>
              te espera.
            </em>
          </h3>

          <p>
            Consulta disponibilidad,
            tarifas y reserva tu
            experiencia frente al
            mar en Orión.
          </p>
        </div>

        <a
          href={
            createGeneralWhatsAppUrl()
          }
          target="_blank"
          rel="noopener noreferrer"
          className="cabins__cta-button"
        >
          <span className="cabins__cta-icon">
            <WhatsAppIcon />
          </span>

          <span>
            Reservar por WhatsApp
          </span>

          <strong>
            →
          </strong>
        </a>
      </div>

      {/* =================================================
          MODAL
      ================================================= */}

      {selectedCabin && (
        <div
          className="cabin-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cabin-modal-title"
          onClick={closeCabin}
        >
          <div
            className="cabin-modal__box"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            {/* CLOSE */}
            <button
              ref={modalCloseRef}
              type="button"
              className="cabin-modal__close"
              onClick={closeCabin}
              aria-label="Cerrar ventana"
            >
              <CloseIcon />
            </button>

            {/* VISUAL */}
            <div className="cabin-modal__visual">
              <img
                src={
                  selectedCabin.image
                }
                alt={
                  selectedCabin.imageAlt
                }
                className="cabin-modal__image"
                onError={(event) => {
                  const fallback =
                    DEFAULT_CABINS[0]
                      ?.image;

                  if (
                    fallback &&
                    event.currentTarget
                      .src !==
                      fallback
                  ) {
                    event.currentTarget.src =
                      fallback;
                  }
                }}
              />

              <div className="cabin-modal__visual-gradient" />

              <div className="cabin-modal__visual-top">
                <span>
                  {
                    selectedCabin.category
                  }
                </span>

                <small>
                  ORION ·{" "}
                  {String(
                    Math.max(
                      1,
                      cabins.findIndex(
                        (item) =>
                          item.id ===
                          selectedCabin.id
                      ) + 1
                    )
                  ).padStart(2, "0")}
                </small>
              </div>

              <div className="cabin-modal__visual-bottom">
                <span>
                  ORION BEACH HOTEL
                </span>

                <strong>
                  LA GUAJIRA
                </strong>
              </div>
            </div>

            {/* CONTENT */}
            <div className="cabin-modal__content">
              <div className="cabin-modal__content-top">
                <span className="cabin-modal__eyebrow">
                  KITE HOUSE · LA GUAJIRA
                </span>

                <span className="cabin-modal__status">
                  <i />
                  DISPONIBLE PARA CONSULTA
                </span>
              </div>

              <h3 id="cabin-modal-title">
                {
                  selectedCabin.title
                }
              </h3>

              <div className="cabin-modal__line" />

              <p>
                {
                  selectedCabin.description
                }
              </p>

              <div className="cabin-modal__details">
                <div>
                  <span>
                    DESTINO
                  </span>

                  <strong>
                    Mayapo · La Guajira
                  </strong>
                </div>

                <div>
                  <span>
                    EXPERIENCIA
                  </span>

                  <strong>
                    Caribe · Kitesurf
                  </strong>
                </div>

                <div>
                  <span>
                    AMBIENTE
                  </span>

                  <strong>
                    Mar · Naturaleza
                  </strong>
                </div>

                <div>
                  <span>
                    RESERVAS
                  </span>

                  <strong>
                    WhatsApp
                  </strong>
                </div>
              </div>

              <a
                href={createWhatsAppUrl(
                  selectedCabin
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="cabin-modal__whatsapp"
              >
                <WhatsAppIcon />

                <span>
                  Consultar disponibilidad
                </span>

                <strong>
                  →
                </strong>
              </a>

              <button
                type="button"
                className="cabin-modal__back"
                onClick={closeCabin}
              >
                <span>
                  ←
                </span>

                Volver a las estancias
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}